import api from './api';

export type PrioridadProgramacion = 'todos' | 'critico' | 'aproducir' | 'sobrestock' | 'sinmovimiento';
export type SnapshotStatus = 'none' | 'processing' | 'ready' | 'error';

export interface ProduccionProgramacionCalcularParams {
  codigoRamo?: string;
  codigoLinea?: string;
  codigoClase?: string;
  nroUbicacion?: number;
  ventanaDias?: number;
  horizonteDias?: number;
  ajusteDemandaPct?: number;
}

export interface ProduccionProgramacionKpis {
  itemsCriticos: number;
  itemsAProducir: number;
  unidadesSugeridas: number;
  coberturaMediana: number;
}

export interface ProduccionProgramacionEstado {
  status: SnapshotStatus;
  isRecalculating: boolean;
  generatedAt?: string | null;
  totalItems?: number | null;
  durationMs?: number | null;
  calculatedBy?: string | null;
  errorMessage?: string | null;
  filtrosCalculo?: ProduccionProgramacionCalcularParams | null;
  kpis?: ProduccionProgramacionKpis | null;
}

export interface ProduccionProgramacionItem {
  codigoArticulo: string;
  descripcion: string;
  codigoRamo: string;
  codigoLinea: string;
  codigoClase: string;
  stockActual: number;
  reservado: number;
  produccionEnCurso: number;
  programado: number;
  oferta: number;
  ventasNetas: number;
  demandaDiaria: number;
  coberturaDias: number;
  stockMin: number;
  stockMax: number;
  cantidadSugerida: number;
  fechaSugerida: string;
  prioridad: string;
  stockNegativo: boolean;
  recetaValida: boolean;
  consejo: string;
  estadoInsumos?: string;
  cantidadFaltante?: number;
  tieneSemielaborado?: boolean;
  lineaSemielaborado?: boolean;
  insumos?: ProduccionProgramacionInsumo[];
}

export interface ProduccionProgramacionInsumo {
  codigoComponente: string;
  descripcion: string;
  cantidadRequerida: number;
  stockDisponible: number;
  cantidadAsignada: number;
  cantidadFaltante: number;
  semielaborado: boolean;
  codProveedor?: number | null;
}

export interface ProduccionProgramacionPreviewLinea {
  codigoArticulo: string;
  descripcion: string;
  cantidad: number;
  fechaSugerida?: string;
}

export interface ProduccionProgramacionPreviewOrden {
  codigoArticulo: string;
  descripcion: string;
  cantidad: number;
  fechaInicio: string;
  puedeGenerar: boolean;
  motivo: string;
  estadoInsumos: string;
  insumos: ProduccionProgramacionInsumo[];
}

export interface ProduccionProgramacionFaltante {
  codigoComponente: string;
  descripcion: string;
  cantidadFaltante: number;
  codProveedor?: number | null;
}

export interface ProduccionProgramacionPreview {
  ordenes: ProduccionProgramacionPreviewOrden[];
  faltantes: ProduccionProgramacionFaltante[];
}

export interface ProduccionArticuloParametros {
  codigoArticulo: string;
  stockSeguridad: number | null;
  loteMinimo: number | null;
  loteMultiplo: number | null;
  leadTimeDias: number | null;
  coberturaObjetivoDias: number | null;
  activo: boolean;
}

export interface ProduccionProgramacionResponse {
  items: ProduccionProgramacionItem[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  kpis: ProduccionProgramacionKpis;
  snapshot: ProduccionProgramacionEstado;
}

const produccionProgramacionService = {
  getEstado: async (): Promise<ProduccionProgramacionEstado> => {
    const { data } = await api.get<ProduccionProgramacionEstado>('/api/ProduccionProgramacion/estado');
    return data;
  },

  calcular: async (params: ProduccionProgramacionCalcularParams): Promise<ProduccionProgramacionEstado> => {
    const { data } = await api.post<ProduccionProgramacionEstado>('/api/ProduccionProgramacion/calcular', {
      codigoRamo: params.codigoRamo || null,
      codigoLinea: params.codigoLinea || null,
      codigoClase: params.codigoClase || null,
      nroUbicacion: params.nroUbicacion || null,
      ventanaDias: params.ventanaDias ?? 30,
      horizonteDias: params.horizonteDias ?? 30,
      ajusteDemandaPct: params.ajusteDemandaPct ?? 0,
    });
    return data;
  },

  getSugerencias: async (params: ProduccionProgramacionCalcularParams & {
    prioridad?: PrioridadProgramacion;
    searchTerm?: string;
    pageNumber: number;
    pageSize: number;
  }): Promise<ProduccionProgramacionResponse> => {
    const { data } = await api.get<ProduccionProgramacionResponse>('/api/ProduccionProgramacion/sugerencias', {
      params: {
        codigoRamo: params.codigoRamo || undefined,
        codigoLinea: params.codigoLinea || undefined,
        codigoClase: params.codigoClase || undefined,
        prioridad: params.prioridad,
        searchTerm: params.searchTerm || undefined,
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
      },
    });
    return data;
  },

  getParametros: async (codigoArticulo: string): Promise<ProduccionArticuloParametros> => {
    const { data } = await api.get<ProduccionArticuloParametros>(
      `/api/ProduccionProgramacion/parametros/${encodeURIComponent(codigoArticulo)}`,
    );
    return data;
  },

  guardarParametros: async (parametros: ProduccionArticuloParametros): Promise<ProduccionArticuloParametros> => {
    const { data } = await api.put<ProduccionArticuloParametros>(
      `/api/ProduccionProgramacion/parametros/${encodeURIComponent(parametros.codigoArticulo)}`,
      parametros,
    );
    return data;
  },

  previewOrdenes: async (body: {
    nroDepositoOrigen: number;
    nroDepositoDestino: number;
    fecha?: string | null;
    lineas: { codigoArticulo: string; cantidad: number; fechaSugerida?: string }[];
  }): Promise<ProduccionProgramacionPreview> => {
    const { data } = await api.post<ProduccionProgramacionPreview>('/api/ProduccionProgramacion/preview-ordenes', body);
    return data;
  },
};

export default produccionProgramacionService;
